import {
  CircleCheckBigIcon,
  CircleEllipsisIcon,
  CirclePauseIcon,
  CircleXIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type TransmissionTaskStatus =
  | "pending"
  | "in_progress"
  | "success"
  | "skipped"
  | "failed";

export type TransmissionTaskItem = {
  id: string;
  label: string;
  status: TransmissionTaskStatus;
  description?: string;
};

function TransmissionTaskStatusIcon({
  status,
}: {
  status: TransmissionTaskStatus;
}) {
  const iconClass = "size-7 shrink-0";

  switch (status) {
    case "success":
    case "skipped":
      return (
        <CircleCheckBigIcon
          className={cn(iconClass, "text-green-600 dark:text-green-500")}
          aria-hidden
        />
      );
    case "failed":
      return (
        <CircleXIcon
          className={cn(iconClass, "text-destructive")}
          aria-hidden
        />
      );
    case "in_progress":
      return (
        <CircleEllipsisIcon
          className={cn(iconClass, "text-primary animate-pulse")}
          aria-hidden
        />
      );
    case "pending":
      return (
        <CirclePauseIcon
          className={cn(iconClass, "text-muted-foreground")}
          aria-hidden
        />
      );
  }
}

function RetailCtmTransmissionTaskList({
  tasks,
  title = "Statut de la transmission CTM",
  titleIcon: TitleIcon,
}: {
  tasks: TransmissionTaskItem[];
  title?: string;
  titleIcon?: LucideIcon;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {TitleIcon ? (
            <TitleIcon className="text-primary size-5 shrink-0" aria-hidden />
          ) : null}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-4">
          {tasks.map((task) => {
            const isMuted = task.status === "pending";

            return (
              <li
                key={task.id}
                className={cn(
                  "flex items-start gap-3",
                  isMuted && "opacity-60",
                )}
              >
                <TransmissionTaskStatusIcon status={task.status} />
                <div className="min-w-0 space-y-0.5">
                  <p
                    className={cn(
                      "text-sm leading-snug font-medium",
                      isMuted && "text-muted-foreground",
                    )}
                  >
                    {task.label}
                  </p>
                  {task.description ? (
                    <p
                      className={cn(
                        "text-sm leading-snug",
                        task.status === "failed"
                          ? "text-destructive"
                          : "text-muted-foreground",
                      )}
                    >
                      {task.description}
                    </p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

export { RetailCtmTransmissionTaskList };
